import React, { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { reduxForm, Field, formValueSelector, change } from 'redux-form'

import { sort, padArray } from '../../../../utils/arrays'
import { getTable, getColumns } from '../../../../utils/databases'
import { getDatabase } from '../../../../ducks/databases'

import { BaseInput } from '../../../Shared/Forms/DynamicInput'
import Button from '../../../Shared/Button'
import Card from '../../../Shared/Card'
import WrappedInput from '../../../Shared/Forms/WrappedInput'
import FormError from '../../../Shared/Forms/FormError'

const FORM_NAME = 'newViewForm'

const getSortOptions = table => {
  let options = []

  for (let column of getColumns(table)) {
    options.push({
      label: `${column.name} - Ascending`,
      value: { column: column.name, direction: 'asc' },
    })

    options.push({
      label: `${column.name} - Descending`,
      value: { column: column.name, direction: 'desc' },
    })
  }

  return options
}

const validate = values => {
  let errors = { body: {} }

  let body = values.body || {}

  if (!body.databaseId) {
    errors.body.databaseId = 'Please choose a source'
  }

  if (!body.table) {
    errors.body.table = 'Please choose a table'
  }

  if (!body.sort) {
    errors.body.sort = 'Please choose a sorting option'
  }

  if (!values.name) {
    errors.name = 'Please name this view'
  }

  return errors
}

const getDatabaseOptions = databases => {
  return databases.map(db => ({
    label: db.name,
    value: db.id,
  }))
}

const getTableOptions = database => {
  if (!database || !database.schema) {
    return []
  }

  return sort(
    database.schema.map(table => ({
      label: table.name,
      value: table.name,
    })),
    opt => opt.label
  )
}

const selector = formValueSelector(FORM_NAME)

const getEntityLabel = entity => {
  return entity.label || entity.column
}

const Form = function NewViewForm({
  onSubmit,
  handleSubmit,
  submitting,
  error,
  databases,
  onCancel,
  isEdit,
}) {
  const dispatch = useDispatch()
  const tableRef = useRef(null)

  const databaseId = useSelector(state => selector(state, 'body.databaseId'))
  const tableName = useSelector(state => selector(state, 'body.table'))
  const database = useSelector(getDatabase(databaseId))

  const databaseOptions = getDatabaseOptions(databases).concat([
    { label: '+ Add New Source', value: 'new' },
  ])

  const tableOptions = getTableOptions(database)

  const table = getTable(database, tableName)

  const sortOptions = table ? getSortOptions(table) : []

  const infoOptions = table
    ? getColumns(table, { primaryKey: true }).map(c => ({
        label: c.displayName || c.name,
        value: { column: c.name, label: c.displayName },
      }))
    : []

  const detailsOptions = table
    ? getColumns(table, { primaryKey: true, hasManys: true }).map(c => ({
        label: c.displayName || c.name,
        value: { column: c.name, label: c.displayName },
      }))
    : []

  useEffect(() => {
    const prevTable = tableRef.current

    tableRef.current = tableName

    if (!prevTable && isEdit) {
      return
    }

    if (!table) {
      return
    }

    const { labelColumn, name } = table

    const infoText = padArray(
      getColumns(table, { labelColumn: false })
        .slice(0, 1)
        .map(c => ({
          column: c.name,
          label: c.displayName,
        })),
      ''
    )

    const detailsFields = padArray(
      detailsOptions.map(opt => opt.value),
      ''
    )

    // Set labelColumn
    dispatch(change(FORM_NAME, 'body.labelColumn', labelColumn))

    // Set sort
    dispatch(
      change(FORM_NAME, 'body.sort', { column: labelColumn, direction: 'asc' })
    )

    // Set infoText
    dispatch(change(FORM_NAME, 'body.infoText', infoText))

    // Set details
    dispatch(change(FORM_NAME, 'body.detailsFields', detailsFields))

    // Set name
    dispatch(change(FORM_NAME, 'name', name))
  }, [tableName])

  useEffect(() => {
    if (database && !table) {
      dispatch(change(FORM_NAME, 'body.table', database.schema[0].name))
    }
  }, [database])

  const labelColumnOptions = table
    ? getColumns(table, { primaryKey: true }).map(c => ({
        label: c.displayName || c.name,
        value: c.name,
      }))
    : []

  return (
    <Card
      component="form"
      onSubmit={handleSubmit}
      actions={
        <>
          <Button text type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button loading={submitting}>{isEdit ? 'Save' : 'Create'}</Button>
        </>
      }
    >
      <Field
        name="body.databaseId"
        label="Source"
        options={databaseOptions}
        component={WrappedInput}
        placeholder="Choose Source..."
      />
      {databaseId && (
        <Field
          name="body.table"
          label="Collection"
          options={tableOptions}
          component={WrappedInput}
        />
      )}
      {databaseId && tableName && (
        <>
          <Field
            jsonOptions
            name="body.sort"
            label="Sort"
            placeholder="Select..."
            component={WrappedInput}
            options={sortOptions}
          />
          <Field
            name="body.labelColumn"
            label="Label"
            placeholder="Select..."
            component={WrappedInput}
            options={labelColumnOptions}
          />
          <Field
            name="body.infoText"
            label="Subtitle"
            placeholder="None"
            component={BaseInput}
            options={infoOptions}
            getLabel={getEntityLabel}
          />
          <Field
            name="body.detailsFields"
            label="Details Page Fields"
            placeholder="None"
            component={BaseInput}
            options={detailsOptions}
            getLabel={getEntityLabel}
          />
          <Field
            name="name"
            label="View Name"
            placeholder={tableName}
            component={WrappedInput}
            autoComplete="off"
          />
        </>
      )}
      {error && <FormError>{error}</FormError>}
    </Card>
  )
}

export default reduxForm({
  form: FORM_NAME,
  validate,
})(Form)
